import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { MasterItem } from 'src/app/Models/masterItem.model';
import { TypeId, VendorItem } from 'src/app/Models/vendorItem.model';
import { MappingService } from '../mapping.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-brand-mapping-management',
  templateUrl: './brand-mapping-management.component.html',
  styleUrls: ['./brand-mapping-management.component.scss']
})
export class BrandMappingManagementComponent {
  vendors: { name: string; vendorId: number }[] = [];
  brands: MasterItem[] = [];
  filteredBrands: MasterItem[] = [];
  brandSearchTerm = '';
  selectedVendorId?: number;
  selectedBrand: MasterItem | null = null;
  vendorSizes: VendorItem[] = [];
  isLoading = false;
  statusMessage = '';
  selectedGeneralMappingIds = new Set<number>();
  private readonly LAST_SELECTED_BRAND_KEY = 'mapping.lastSelectedBrand';
  private readonly brandSpecificMappingKeys = new Set<string>();
  private readonly brandSpecificMappingIds = new Map<string, number>();
  private queryBrandId?: number;
  private readonly onDestroy$ = new Subject<void>();

  constructor(
    private mappingService: MappingService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.loadVendors();
    this.loadBrands();
    this.applyQueryParams();
  }

  private applyQueryParams(): void {
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
      const vendorId = Number(params.get('vendorId'));
      const brandId = Number(params.get('brandId'));
      if (vendorId) {
        this.selectedVendorId = vendorId;
      }
      if (brandId) {
        this.queryBrandId = brandId;
      }
      // If a brandId is in the URL, wait until loadBrands() resolves selectedBrand
      // before calling loadVendorSizes() — otherwise we'd race a general-list request
      // against the brand-specific one and the slower response would overwrite the data.
      if (this.selectedVendorId && !brandId) {
        this.loadVendorSizes();
      }
    });
  }

  private loadVendors(): void {
    this.mappingService.getOrganizations(2).pipe(takeUntil(this.onDestroy$)).subscribe({
      next: (organizations) => {
        const safeOrganizations = this.ensureArray<any>(organizations);
        this.vendors = safeOrganizations.map(org => ({ name: org.name, vendorId: org.vendorId }));
      },
      error: (error) => {
        console.error('Failed to load vendors', error);
      }
    });
  }

  private loadBrands(): void {
    this.mappingService.getMasterList(TypeId.Brands as unknown as TypeId).pipe(takeUntil(this.onDestroy$)).subscribe({
      next: (brands) => {
        this.brands = this.ensureArray<MasterItem>(brands);
        this.filteredBrands = this.brands;
        const selectedId = this.queryBrandId || Number(localStorage.getItem(this.LAST_SELECTED_BRAND_KEY));
        if (!this.selectedBrand && selectedId) {
          this.selectedBrand = this.brands.find(item => item.id === selectedId) || null;
        }
        if (this.selectedVendorId && (selectedId || !this.queryBrandId)) {
          this.loadVendorSizes();
        }
      },
      error: (error) => {
        console.error('Failed to load brands', error);
      }
    });
  }

  get selectedVendorName(): string {
    if (!this.selectedVendorId) {
      return 'Select Vendor';
    }
    return this.vendors.find(v => v.vendorId === this.selectedVendorId)?.name || 'Select Vendor';
  }

  get selectedBrandName(): string {
    return this.selectedBrand?.name || 'Select Brand';
  }

  selectVendor(vendorId: number): void {
    this.selectedVendorId = vendorId;
    this.loadVendorSizes();
  }

  selectBrand(brand: MasterItem): void {
    this.selectedBrand = brand;
    this.brandSearchTerm = '';
    this.filteredBrands = this.brands;
    localStorage.setItem(this.LAST_SELECTED_BRAND_KEY, String(brand.id));
    this.loadVendorSizes();
  }

  filterBrands(): void {
    const term = this.brandSearchTerm.toLowerCase().trim();
    this.filteredBrands = term
      ? this.brands.filter(brand => brand.name.toLowerCase().includes(term))
      : this.brands;
  }

  goBack(): void {
    this.router.navigate(['/main/products/mapping'], {
      queryParams: {
        vendorId: this.selectedVendorId ?? null,
        brandId: this.selectedBrand?.id ?? null,
        taxonomy: TypeId.Sizes
      }
    });
  }

  loadVendorSizes(): void {
    if (!this.selectedVendorId) {
      return;
    }
    this.isLoading = true;
    this.statusMessage = '';
    const sizes$ = this.selectedBrand
      ? this.mappingService.getVendorBrandSizes(this.selectedVendorId, this.selectedBrand.id)
      : this.mappingService.getVendorList(this.selectedVendorId, TypeId.Sizes as unknown as TypeId);

    sizes$.pipe(takeUntil(this.onDestroy$)).subscribe({
      next: (sizes) => {
        this.vendorSizes = this.selectedBrand
          ? this.mapVendorBrandSizesResponse(sizes)
          : this.ensureArray<VendorItem>(sizes);
        this.brandSpecificMappingKeys.clear();
        this.brandSpecificMappingIds.clear();
        this.selectedGeneralMappingIds.clear();
        if (this.selectedBrand) {
          this.vendorSizes.forEach((size) => {
            if (!size?.id) {
              return;
            }
            const key = this.getKey(size.id);
            if ((size as any).hasBrandSpecificMapping) {
              this.brandSpecificMappingKeys.add(key);
            }
            // Track the underlying MappingTable id for ANY brand-specific row (including
            // orphans where hasBrandSpecificMapping is false) so the delete action can clean
            // them up via hasBrandSpecificMappingRow().
            const mappingId = (size as any).brandSpecificMappingId;
            if (typeof mappingId === 'number') {
              this.brandSpecificMappingIds.set(key, mappingId);
            }
          });
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Failed to load vendor sizes', error);
        this.isLoading = false;
      }
    });
  }

  isGeneralMapped(size: VendorItem): boolean {
    return !!(size as any).hasGeneralMapping || !!size.systemItemId;
  }

  canCreateBrandMapping(size: VendorItem): boolean {
    return (
      !!this.selectedBrand &&
      this.isGeneralMapped(size) &&
      !this.isBrandMapped(size) &&
      !this.hasBrandSpecificMappingRow(size)
    );
  }

  isBrandMapped(size: VendorItem): boolean {
    return !!this.selectedBrand && this.brandSpecificMappingKeys.has(this.getKey(size.id));
  }

  /**
   * True when a brand-specific MappingTable row exists for this size, regardless of whether it
   * actually points to a SystemItemId. Used for the delete action so admins can clean up orphan
   * rows even when isBrandMapped() (which requires a complete mapping) is false.
   */
  hasBrandSpecificMappingRow(size: VendorItem): boolean {
    if (!this.selectedBrand) {
      return false;
    }
    const inlineId = (size as any)?.brandSpecificMappingId;
    if (inlineId != null) {
      return true;
    }
    return size?.id != null && this.brandSpecificMappingIds.has(this.getKey(size.id));
  }

  async createBrandSpecific(size: VendorItem): Promise<void> {
    if (!this.selectedBrand) {
      alert('Please select a brand first.');
      return;
    }
    if (!size?.id) {
      this.statusMessage = 'Cannot create brand mapping: missing general mapping id for this row.';
      return;
    }
    const key = this.getKey(size.id);
    this.isLoading = true;
    this.statusMessage = '';
    try {
      const result = await firstValueFrom(
        this.mappingService.createBrandSpecificMapping(size.id, this.selectedBrand.id)
      );
      if (result) {
        this.brandSpecificMappingKeys.add(key);
        this.selectedGeneralMappingIds.delete(size.id);
        const createdId = this.extractResponseId(result);
        if (createdId) {
          this.brandSpecificMappingIds.set(key, createdId);
        }
        this.statusMessage = 'Brand mapping created.';
        this.toastr.success('Brand mapping created.', 'Success');
        this.loadVendorSizes();
      } else {
        this.statusMessage = 'Mapping already exists or could not be created.';
        this.toastr.warning('Mapping already exists or could not be created.');
      }
    } catch (error) {
      console.error('Failed to create brand-specific mapping', error);
      this.statusMessage = 'Failed to create brand mapping. See console for details.';
      this.toastr.error('Failed to create brand mapping.', 'Error');
    } finally {
      this.isLoading = false;
    }
  }

  async deleteBrandSpecific(size: VendorItem): Promise<void> {
    if (!this.selectedBrand || !size?.id) {
      return;
    }
    const key = this.getKey(size.id);
    const brandSpecificMappingId =
      (size as any).brandSpecificMappingId ??
      this.brandSpecificMappingIds.get(key);

    if (!brandSpecificMappingId) {
      console.warn('No brandSpecificMappingId found for size', size);
      this.statusMessage = 'Cannot delete: brand mapping id not available for this row. Please reload the list.';
      return;
    }

    this.isLoading = true;
    this.statusMessage = '';
    try {
      await firstValueFrom(
        this.mappingService.deleteMapping(brandSpecificMappingId)
      );
      this.brandSpecificMappingKeys.delete(key);
      this.brandSpecificMappingIds.delete(key);
      this.statusMessage = 'Brand mapping deleted.';
      this.toastr.success('Brand mapping deleted.', 'Success');
      this.loadVendorSizes();
    } catch (error) {
      console.error('Failed to delete brand-specific mapping', error);
      this.statusMessage = 'Failed to delete brand mapping. See console for details.';
      this.toastr.error('Failed to delete brand mapping.', 'Error');
    } finally {
      this.isLoading = false;
    }
  }

  async createAllForBrand(): Promise<void> {
    if (!this.selectedBrand) {
      alert('Please select a brand first.');
      return;
    }
    this.statusMessage = '';
    const selectedIds = Array.from(this.selectedGeneralMappingIds).filter(id => !!id);
    const candidateIds = selectedIds.length
      ? selectedIds
      : this.vendorSizes
        .filter(size => this.canCreateBrandMapping(size))
        .map(size => size.id)
        .filter((id): id is number => !!id);

    if (!candidateIds.length) {
      this.statusMessage = 'No eligible sizes available for brand mapping. Make sure the selected vendor has generally-mapped sizes that are not yet brand-mapped for this brand.';
      return;
    }

    this.isLoading = true;
    try {
      const result = await firstValueFrom(
        this.mappingService.createBrandSpecificSizes({
          brandId: this.selectedBrand.id,
          generalMappingIds: candidateIds
        })
      );
      if (result) {
        candidateIds.forEach((id) => {
          const key = this.getKey(id);
          this.brandSpecificMappingKeys.add(key);
          this.selectedGeneralMappingIds.delete(id);
        });
        this.statusMessage = `${candidateIds.length} brand-specific mappings created.`;
        this.toastr.success(`${candidateIds.length} brand mapping(s) created.`, 'Success');
        this.loadVendorSizes();
      } else {
        this.statusMessage = 'Could not create selected brand mappings.';
        this.toastr.warning('Could not create selected brand mappings.');
      }
    } catch (error) {
      console.error('Failed to create selected brand mappings', error);
      this.statusMessage = 'Failed to create brand mappings. See console for details.';
      this.toastr.error('Failed to create brand mappings.', 'Error');
    } finally {
      this.isLoading = false;
    }
  }

  private getKey(vendorItemId: number): string {
    return `${this.selectedVendorId ?? 0}-${this.selectedBrand?.id ?? 0}-${vendorItemId}`;
  }

  private mapVendorBrandSizesResponse(response: any): VendorItem[] {
    const rows = this.ensureArray<any>(response?.sizes ?? response);
    return rows.map((row) => {
      const generalMappingId = row?.generalMappingId ?? row?.id;
      const generalSystemItemId = row?.generalSystemItemId ?? row?.systemItemId ?? 0;
      const hasGeneralMapping =
        typeof row?.hasGeneralMapping === 'boolean'
          ? row.hasGeneralMapping
          : !!generalSystemItemId;
      const hasBrandSpecificMapping = !!row?.hasBrandSpecificMapping;

      return {
        id: generalMappingId,
        idName: row?.rawSize ?? row?.idName ?? '',
        systemItemId: hasGeneralMapping ? generalSystemItemId : 0,
        systemItemValue: row?.generalSystemSizeValue ?? row?.systemItemValue ?? null,
        sizeTypeId: row?.sizeTypeId ?? null,
        sizeTypeName: row?.sizeTypeName ?? null,
        typeId: TypeId.Sizes,
        supplierId: this.selectedVendorId as any,
        hasGeneralMapping: hasGeneralMapping,
        hasBrandSpecificMapping: hasBrandSpecificMapping,
        brandSpecificMappingId: row?.brandSpecificMappingId ?? null,
        brandSpecificSystemItemId: row?.brandSpecificSystemItemId ?? null,
        brandSpecificSystemItemValue: row?.brandSpecificSystemSizeValue ?? null,
        genderId: row?.genderId ?? null,
        genderName: row?.genderName ?? null
      } as VendorItem;
    });
  }

  isSelected(size: VendorItem): boolean {
    return !!size?.id && this.selectedGeneralMappingIds.has(size.id);
  }

  /** Eligible rows for bulk selection (general mapped, not yet brand-mapped for selected brand). */
  private getEligibleSizesForSelectAll(): VendorItem[] {
    return this.vendorSizes.filter((s) => this.canCreateBrandMapping(s) && s.id);
  }

  get selectAllEligibleChecked(): boolean {
    const eligible = this.getEligibleSizesForSelectAll();
    if (!eligible.length) {
      return false;
    }
    return eligible.every((s) => this.selectedGeneralMappingIds.has(s.id!));
  }

  get selectAllEligibleDisabled(): boolean {
    return !this.selectedBrand || this.isLoading || !this.getEligibleSizesForSelectAll().length;
  }

  toggleSelectAll(checked: boolean): void {
    if (!this.selectedBrand) {
      return;
    }
    const eligible = this.getEligibleSizesForSelectAll();
    if (!eligible.length) {
      return;
    }
    if (checked) {
      eligible.forEach((s) => {
        if (s.id) {
          this.selectedGeneralMappingIds.add(s.id);
        }
      });
      return;
    }
    eligible.forEach((s) => {
      if (s.id) {
        this.selectedGeneralMappingIds.delete(s.id);
      }
    });
  }

  toggleSelection(size: VendorItem, checked: boolean): void {
    if (!size?.id || !this.canCreateBrandMapping(size)) {
      return;
    }
    if (checked) {
      this.selectedGeneralMappingIds.add(size.id);
      return;
    }
    this.selectedGeneralMappingIds.delete(size.id);
  }

  private ensureArray<T>(value: any): T[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (value && Array.isArray(value.sizes)) {
      return value.sizes;
    }
    if (value && Array.isArray(value.data)) {
      return value.data;
    }
    if (value && Array.isArray(value.items)) {
      return value.items;
    }
    if (value && Array.isArray(value.result)) {
      return value.result;
    }
    return [];
  }

  private extractResponseId(result: any): number | undefined {
    if (typeof result === 'number') {
      return result;
    }
    if (result && typeof result.id === 'number') {
      return result.id;
    }
    if (result && typeof result.brandSpecificMappingId === 'number') {
      return result.brandSpecificMappingId;
    }
    if (result && result.data && typeof result.data.id === 'number') {
      return result.data.id;
    }
    return undefined;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
